import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator,
} from '../ui/command';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  LayoutDashboard, Users, BookOpen, Megaphone, BookmarkCheck, Calendar, Heart, Compass, Brain,
  MessageSquare, Handshake, FileText, CreditCard, ShieldCheck, DollarSign, IdCard, Share2, Mic,
  FolderOpen, Building2, Inbox, Sun, Moon, LogOut,
} from 'lucide-react';

// Role-based destinations — mirror the dashboard nav so ⌘K can jump anywhere instantly.
// CaraLoom 2.0: Family group reframed around Care suite (coordination > marketplace search);
// Clinician group surfaces the V2 RCM features (Denial Prediction, Executive Dashboard).
// V1-archived routes (Post a Need, Saved Searches) are intentionally NOT included so the
// command palette never deep-links into a FeatureGate dead-end. Code is preserved at the
// route level so it can be resurrected per the `caraloom_v2_ia.js` archive plan.
const DESTINATIONS = {
  patient: [
    { group: 'Your care', items: [
      { label: 'This Week (Digest)', to: '/patient/this-week', icon: Sun },
      { label: 'Care plan', to: '/patient/care-plan', icon: FileText },
      { label: 'Care Circle', to: '/patient/care-circle', icon: Users },
      { label: 'Care Navigator', to: '/patient/navigator', icon: Compass },
      { label: 'Daily Check-in', to: '/patient/checkin', icon: Heart },
      { label: 'Family Portal', to: '/patient/family', icon: Users },
    ] },
    { group: 'Find & match', items: [
      { label: 'Find Clinicians', to: '/patient/find-clinicians', icon: Users },
      { label: 'Browse Marketplace', to: '/marketplace', icon: BookOpen },
    ] },
    { group: 'Activity', items: [
      { label: 'Appointments', to: '/patient/dashboard#appointments', icon: Calendar },
      { label: 'Messages', to: '/patient/messages', icon: MessageSquare },
      { label: 'Offers & Bookings', to: '/patient/offers', icon: Handshake },
    ] },
    { group: 'Records & Billing', items: [
      { label: 'Billing & Statements', to: '/patient/billing', icon: CreditCard },
      { label: 'Documents', to: '/patient/documents', icon: FileText },
      { label: 'Privacy & Data', to: '/privacy/dashboard', icon: ShieldCheck },
    ] },
  ],
  clinician: [
    { group: 'Opportunities', items: [
      { label: 'Open Shifts', to: '/workforce/shifts', icon: Megaphone },
      { label: 'Care Requests', to: '/clinician/care-requests', icon: Megaphone },
      { label: 'Case Requests', to: '/clinician/dashboard#cases', icon: Users },
      { label: 'Offers Inbox', to: '/clinician/offers', icon: Handshake },
      { label: 'Engagements', to: '/clinician/dashboard#engagements', icon: Inbox },
      { label: 'Provider Engagements', to: '/clinician/dashboard#agency', icon: Building2 },
    ] },
    { group: 'Care Delivery', items: [
      { label: 'Schedule', to: '/clinician/dashboard#schedule', icon: Calendar },
      { label: 'Clinical Notes', to: '/clinician/notes', icon: FileText },
      { label: 'Voice Notes', to: '/clinician/voice-notes', icon: Mic },
      { label: 'Messages', to: '/clinician/messages', icon: MessageSquare },
    ] },
    { group: 'Practice & Profile', items: [
      { label: 'Practice Coach', to: '/clinician/coach', icon: Compass },
      { label: 'Practice Profile', to: '/clinician/dashboard#profile', icon: IdCard },
      { label: 'Public Profile', to: '/clinician/dashboard#share', icon: Share2 },
    ] },
    { group: 'Revenue Cycle & Operations', items: [
      { label: 'Billing & RCM', to: '/clinician/billing', icon: DollarSign },
      { label: 'Denial Prediction', to: '/clinician/denial-prediction', icon: ShieldCheck },
      { label: 'Executive Dashboard', to: '/operations/executive', icon: LayoutDashboard },
      { label: 'P&L (CFO view)', to: '/operations/pnl', icon: DollarSign },
      { label: 'Documents', to: '/clinician/documents', icon: FolderOpen },
      { label: 'Privacy & Data', to: '/privacy/dashboard', icon: ShieldCheck },
    ] },
  ],
};

/**
 * Global ⌘K / Ctrl+K command palette. Lets any signed-in user jump to any
 * destination or run a quick action without hunting through the sidebar.
 * Also opens via the `open-command-palette` window event (sidebar trigger).
 */
export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = () => setOpen(true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('open-command-palette', onOpen);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('open-command-palette', onOpen);
    };
  }, []);

  const run = useCallback((fn) => {
    setOpen(false);
    setTimeout(fn, 0);
  }, []);

  const go = useCallback((to) => run(() => {
    navigate(to);
    const hash = to.split('#')[1];
    if (hash) setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' }), 250);
  }), [navigate, run]);

  if (!user) return null;
  const dashHome = user.role === 'clinician' ? '/clinician/dashboard'
    : user.role === 'admin' ? '/admin' : '/patient/dashboard';
  const groups = DESTINATIONS[user.role] || [];

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search pages and actions… (try 'billing', 'messages')" data-testid="command-palette-input" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Go to">
          <CommandItem value="Dashboard home overview" onSelect={() => go(dashHome)} data-testid="cmd-dashboard">
            <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
          </CommandItem>
        </CommandGroup>
        {groups.map((grp) => (
          <CommandGroup key={grp.group} heading={grp.group}>
            {grp.items.map((it) => {
              const Icon = it.icon;
              return (
                <CommandItem
                  key={it.label}
                  value={`${it.label} ${grp.group}`}
                  onSelect={() => go(it.to)}
                  data-testid={`cmd-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                >
                  <Icon className="mr-2 h-4 w-4" /> {it.label}
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}
        <CommandSeparator />
        <CommandGroup heading="Actions">
          <CommandItem value="Toggle theme dark light mode appearance" onSelect={() => run(toggleTheme)} data-testid="cmd-toggle-theme">
            {theme === 'light' ? <Moon className="mr-2 h-4 w-4" /> : <Sun className="mr-2 h-4 w-4" />}
            Toggle {theme === 'light' ? 'dark' : 'light'} mode
          </CommandItem>
          <CommandItem value="Sign out log out logout exit" onSelect={() => run(() => { logout(); navigate('/'); })} data-testid="cmd-logout">
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
